import React from 'react'
import { BaseView } from '../BaseView'
import { BaseViewProps } from '../BaseView/types'
import { useTheme } from '@/hooks/useTheme'
import { responsiveAdapter } from '@/utils'

export interface BaseSafeFormViewProps extends BaseViewProps {
  enableScroll?: boolean
  disableKeyboardDismiss?: boolean
  paddingHorizontal?: number
}

export const BaseSafeFormView: React.FC<BaseSafeFormViewProps> = ({
  children,
  enableScroll = false,
  disableKeyboardDismiss = false,
  bgColor,
  paddingHorizontal = 12, // Default horizontal padding like React Native
  ...rest
}) => {
  const theme = useTheme()

  // Default background to screen color if not provided
  const backgroundColor = bgColor || theme.colors.background.screen

  // Apply responsive horizontal padding
  const responsivePaddingHorizontal = responsiveAdapter(paddingHorizontal)

  const handleFormClick = (e: React.MouseEvent) => {
    if (!disableKeyboardDismiss && e.target === e.currentTarget) {
      // Blur any focused input to dismiss keyboard on mobile
      const activeElement = document.activeElement as HTMLElement
      if (activeElement && activeElement.blur) {
        activeElement.blur()
      }
    }
  }

  return (
    <BaseView
      className="h-full"
      bgColor={backgroundColor}
      onClick={handleFormClick}
      style={{
        // Use height instead of minHeight to prevent scroll
        height: '100vh',
        boxSizing: 'border-box',
        overflow: 'hidden',
        // Handle safe areas on mobile using calc to subtract from total height
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: `max(${responsivePaddingHorizontal}px, env(safe-area-inset-left))`,
        paddingRight: `max(${responsivePaddingHorizontal}px, env(safe-area-inset-right))`,
      }}
      {...rest}
    >
      {enableScroll ? (
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            // Ensure scrollable content doesn't exceed container
            height: '100%',
          }}
        >
          {children}
        </div>
      ) : (
        <div
          style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {children}
        </div>
      )}
    </BaseView>
  )
}
